<template>
	<MobiscrollTooltip v-if="hasOverflow" :style="style" :content="tooltipText">
		<div class="overflow-hidden whitespace-nowrap">
			<span ref="textElement">
				<slot v-bind="{ tooltipText }"> {{ tooltipText }} </slot>
			</span>
		</div>

		<template #content>
			{{ tooltipText }}
		</template>
	</MobiscrollTooltip>

	<!-- Do not render the entire tooltip machinery at all if there is no text overflow -->
	<div v-else class="overflow-hidden whitespace-nowrap" :style="style">
		<span ref="textElement">
			<slot v-bind="{ tooltipText }"> {{ tooltipText }} </slot>
		</span>
	</div>
</template>

<script setup lang="ts">
/*
NOTE: When using this component's slot like:
`<MobiscrollTooltipIfEllipsis tooltip-text="Lorem ipsum">
	{{ propValue }}
</MobiscrollTooltipIfEllipsis>`
make sure to use (if you need to wrap slot's content) a `span` instead of a `div`:
`<MobiscrollTooltipIfEllipsis tooltip-text="Lorem ipsum">
	<span class="font-body-l">{{ propValue }}</span>
</MobiscrollTooltipIfEllipsis>`
With a `div` rendering the ellipsis and tooltip can be problematic
*/
import { isInt, useTextOverflow } from '@bluebrick/saas-ui'
import { type Ref, type StyleValue, computed, ref, toRefs } from 'vue'
import MobiscrollTooltip from '@/common/components/MobiscrollTooltip.vue'

const props = defineProps<{
	tooltipText: string
	maxWidth?: number // in rem
}>()

const { maxWidth } = toRefs(props) as {
	maxWidth: Ref<number | undefined>
}

const textElement = ref<HTMLSpanElement>()
const { hasOverflow } = useTextOverflow(textElement)

const style = computed<StyleValue>(() => {
	if (isInt(maxWidth.value) && maxWidth.value > 0) {
		return {
			maxWidth: `${maxWidth.value}rem`
		}
	}

	return {}
})
</script>
